import React, { useEffect, useState } from "react"
import { TypeaheadProvider, ExtendedProvidersTypeaheadProvider } from "#/pages/provider-finder/types/Providers"
import { TypeaheadDoula } from "#/pages/provider-finder/recommend-doula/RecommendDoulaForm"
import { findMatchedSubstring } from "#/pages/provider-finder/utils/findMatchedSubstring"
import { getPiiProps } from "#/services/tracking/pii"
import { Box, Typography } from "@carrotfertility/carotene-core"

export function useDebouncedSearch(
  setProviderNameValue: (input: string) => void,
  setOpen: (open: boolean) => void
): {
  inputValue: string | null
  loading: boolean
  setInputValue: (value: string | null) => void
  searchValue: string | null
} {
  const [inputValue, setInputValue] = useState<string | null>(null)
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const trimmedValue = inputValue?.trim()
      if (trimmedValue?.length) {
        setSearchValue(trimmedValue)
      } else {
        setSearchValue(null)
        setOpen(false)
      }
      setLoading(false)
    }, 500)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [inputValue, setOpen])

  function handleSetInputValue(newValue: string | null) {
    setInputValue(newValue)
    setProviderNameValue(newValue || "")
    // Always set loading to true when input changes, regardless of length
    setLoading(true)

    if (newValue) {
      setOpen(true)
    }
  }

  return { inputValue, loading, setInputValue: handleSetInputValue, searchValue }
}

interface ProviderPrediction extends TypeaheadProvider {
  isDoulaRecommendationOption?: boolean
}

export function renderAutocompleteProviderOption(
  props: React.HTMLAttributes<HTMLLIElement>,
  providerPrediction: ProviderPrediction | ExtendedProvidersTypeaheadProvider | TypeaheadDoula | null,
  currentInputValue: string | null
): React.ReactElement {
  if (!providerPrediction) {
    return <Box component="li" {...props} />
  }
  const { name, uuid } = providerPrediction
  const matchedSubstringDetails = name
  const { length, offset } = findMatchedSubstring(name, currentInputValue) || { length: 0, offset: 0 }
  const beforeMatchedSubstring = matchedSubstringDetails ? name.slice(0, offset) : name
  const matchedSubstring = matchedSubstringDetails ? name.slice(offset, offset + length) : ""
  const afterMatchedSubstring = matchedSubstringDetails ? name.slice(offset + length) : ""

  return (
    <Box
      component="li"
      key={`${name}-${uuid}`}
      sx={{ "::before": { display: "none" } }} // main.scss puts styles we don't want here.
    >
      <Box {...getPiiProps()} {...props} component="span">
        <Typography sx={(theme) => ({ color: theme.palette.text.primary })} component="span">
          {beforeMatchedSubstring}
          <Typography sx={{ fontWeight: "bold" }} variant="inherit" component="span">
            {matchedSubstring}
          </Typography>
          {afterMatchedSubstring}
        </Typography>
      </Box>
    </Box>
  )
}
